import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, ClosableDrawer } from '@atoms/layout';
import { Divider, Grid, Stack, styled, Typography } from '@mui/material';
import { Button } from '@atoms/buttons';
import { useAssignShiftDrawerStore, useCalendarShiftsStore, useShiftsAssignment } from '@libs/store/shifts';
import { useNotification } from '@libs/snackbar';
import { ShiftsIcon } from '@atoms/icons';
import { DataGrid, EmptyListPlaceholder } from '@molecules/dataDisplay';
import { useShiftListColumns } from './shiftListColumns';
import { parseError } from '@libs/api/errors';
import { ResourceItem } from '@pages/schedule/calendar/weekly/calendar/calendar';
import dayjs from 'dayjs';
import { ConfirmCreateDoubleShiftDialog } from '@pages/schedule/dialogs';
import { StaffListItem } from '@libs/models/talents/talent';

type DrawerProps = {
  onSaveCall?: () => void;
};

export function AssignShifts({ onSaveCall }: DrawerProps) {
  const { showErrorIntl } = useNotification();
  const { assignShift, checkAssignOverlap, requestAssignShiftOnExternal } = useShiftsAssignment();

  const { open, closeDrawer, currentProviderInfo, day, providerAssignments } = useAssignShiftDrawerStore();
  const { unassignedShifts } = useCalendarShiftsStore();

  const [sharingProgress, setSharingProgress] = useState(false);
  const [selectedShifts, setSelectedShifts] = useState<string[]>([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [doubleShiftId, setDoubleShiftId] = useState<string | null>(null);

  const localizationSubset = 'shifts.assign.drawer';

  const loading = sharingProgress;

  const shiftRows = unassignedShifts.filter((shift) => {
    return (
      dayjs(shift.startDate).isSame(day, 'day') &&
      currentProviderInfo?.qualifications.some((qualification) =>
        shift.nurseQualifications.some((shiftQualification) => shiftQualification.id === qualification.id),
      ) &&
      !['expired', 'canceled'].includes(shift.status.toLowerCase())
    );
  });

  const handleClose = () => {
    closeDrawer();
  };

  const handleSubmit = async () => {
    if (!currentProviderInfo) return;
    setSharingProgress(true);
    for (let i = 0; i < selectedShifts.length; i++) {
      const shiftId = selectedShifts[i];
      const shift = shiftRows.find((s) => s.id === shiftId);
      if (shift?.id) {
        const overlapInfo = await checkAssignOverlap({
          shiftID: shift.id,
          assigneeId: currentProviderInfo.id,
        });
        if (overlapInfo.canCreateDoubleShift) {
          setDoubleShiftId(shiftId);
          setOpenConfirmDialog(true);
          setSharingProgress(false);
          return; // interrupt all actions in case if overlap is detected
        }
      }
    }
    if (currentProviderInfo.staffingType === 'Agency') {
      await performAssignExternal(selectedShifts, currentProviderInfo.id);
    } else {
      await performAssign(selectedShifts, currentProviderInfo.id);
    }
    setSharingProgress(false);
  };

  const performAssign = async (shiftIds: string[], resourceId: string, doubleShiftId?: string | null) => {
    try {
      for (let i = 0; i < shiftIds.length; i++) {
        const shiftId = shiftIds[i];
        await assignShift(shiftId, resourceId, doubleShiftId);
      }
      onSaveCall && onSaveCall();
      handleClose();
    } catch (e) {
      if (e instanceof Error) {
        const error = parseError(e, `shifts.shareErrors`);
        showErrorIntl(error.message);
      }
    }
  };

  const performAssignExternal = async (shiftIds: string[], resourceId: string) => {
    try {
      for (let i = 0; i < shiftIds.length; i++) {
        const shiftId = shiftIds[i];
        await requestAssignShiftOnExternal(shiftId, resourceId);
      }
      onSaveCall && onSaveCall();
      handleClose();
    } catch (e) {
      if (e instanceof Error) {
        const error = parseError(e, `shifts.shareErrors`);
        showErrorIntl(error.message);
      }
    }
  };

  return (
    <>
      <ClosableDrawer
        open={open}
        loading={loading}
        onClose={handleClose}
        disableControlsDivider
        controls={
          <>
            <Divider sx={(theme) => ({ marginBottom: theme.spacing(2) })} />
            <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
              <MenuButton variant="contained" onClick={handleSubmit} disabled={loading || selectedShifts.length === 0}>
                <FormattedMessage id={`${localizationSubset}.menuButtons.assign.label`} />
              </MenuButton>
              <Box flex={1} />
              <MenuButton
                onClick={handleClose}
                sx={(theme) => ({ color: theme.palette.text.secondary })}
                disabled={loading}
              >
                <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
              </MenuButton>
            </Stack>
          </>
        }
      >
        <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '300px', sm: '600px' }}>
          {/* Header */}
          <Stack direction={'row'}>
            <Typography variant="h5">
              <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
            </Typography>
            <Box flex={1} />
            <Typography variant="h5">{day?.format('dddd, MMMM D')}</Typography>
          </Stack>

          <Grid container>
            <Grid item xs={12}>
              <ResourceItem resourceInfo={currentProviderInfo as StaffListItem} assignments={providerAssignments} />
            </Grid>
            {/* <Grid item xs={1}>

          </Grid> */}
            <Grid item xs={12}>
              <DataGrid
                rows={shiftRows}
                loading={loading}
                rowHeight={64}
                hideFooter
                columns={useShiftListColumns()}
                sx={{ minHeight: '300px' }}
                checkboxSelection
                rowCount={shiftRows.length}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setSelectedShifts(newRowSelectionModel.map((v) => v.toString()));
                }}
                rowSelectionModel={selectedShifts}
                noRowsOverlay={
                  <EmptyListPlaceholder
                    icon={<ShiftsIcon active />}
                    title={<FormattedMessage id="shifts.table.noRows.title" />}
                    subtitle={<FormattedMessage id="shifts.table.noRows.subtitle" />}
                  />
                }
              />
            </Grid>
          </Grid>
        </Stack>
      </ClosableDrawer>
      <ConfirmCreateDoubleShiftDialog
        open={openConfirmDialog}
        onClose={() => {
          setDoubleShiftId(null);
          setOpenConfirmDialog(false);
        }}
        onSubmit={async () => {
          if (currentProviderInfo && currentProviderInfo.staffingType === 'Agency') {
            await performAssignExternal(selectedShifts, currentProviderInfo.id);
          } else {
            await performAssign(selectedShifts, currentProviderInfo?.id as string, doubleShiftId);
          }
        }}
      />
    </>
  );
}

const MenuButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(16.75),
}));
